import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Package, ShoppingCart, MessageSquare, DollarSign, Tag, ArrowRight } from 'lucide-react';
import { useSellerStats } from '../hooks/queries/sellerQueries';

const Dashboard: React.FC = () => {
  const { t } = useTranslation('dashboard');
  const { seller } = useAuth();
  const { data: stats, isLoading } = useSellerStats(!!seller);

  if (isLoading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-primary" />
      </div>
    );
  }

  const statCards = [
    {
      title: t('stats.totalProducts'),
      value: stats?.products.total || 0,
      subtitle: `${stats?.products.active || 0} ${t('stats.activeProducts')}`,
      icon: Package,
      color: 'bg-blue-500',
      link: '/products',
    },
    {
      title: t('stats.totalOrders'),
      value: stats?.orders.total || 0,
      subtitle: `${stats?.orders.pending || 0} ${t('stats.pendingOrders')}`,
      icon: ShoppingCart,
      color: 'bg-green-500',
      link: '/orders',
    },
    {
      title: t('stats.totalOffers'),
      value: stats?.bids.total || 0,
      subtitle: `${stats?.bids.pending || 0} ${t('stats.pendingOffers')}`,
      icon: Tag,
      color: 'bg-purple-500',
      link: '/offers',
    },
    {
      title: t('stats.totalEarnings'),
      value: `$${(stats?.earnings.total || 0).toLocaleString()}`,
      subtitle: t('stats.allTime'),
      icon: DollarSign,
      color: 'bg-yellow-500',
      link: '/payments',
    },
  ];

  const pendingOrders = stats?.orders.pending || 0;
  const pendingOffers = stats?.bids.pending || 0;

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{t('title')}</h1>
          <p className="text-sm md:text-base text-gray-600 mt-1 md:mt-2">
            {t('subtitle')}, {seller?.name}!
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <Link
              key={card.link}
              to={card.link}
              className="card p-4 md:p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs md:text-sm text-gray-600 mb-1">{card.title}</p>
                  <p className="text-xl md:text-2xl font-bold text-gray-900">{card.value}</p>
                  <p className="text-xs text-gray-500 mt-1">{card.subtitle}</p>
                </div>
                <div className={`${card.color} p-3 rounded-lg`}>
                  <Icon className="text-white" size={24} />
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {(pendingOrders > 0 || pendingOffers > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {pendingOrders > 0 && (
            <Link
              to="/orders"
              className="card p-4 md:p-6 bg-yellow-50 border border-yellow-200 hover:bg-yellow-100 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-yellow-900">{t('quickActions.pendingOrders')}</h3>
                  <p className="text-sm text-yellow-700 mt-1">
                    {pendingOrders} {t('quickActions.ordersNeedAttention')}
                  </p>
                </div>
                <ArrowRight className="text-yellow-600" size={20} />
              </div>
            </Link>
          )}
          {pendingOffers > 0 && (
            <Link
              to="/offers"
              className="card p-4 md:p-6 bg-purple-50 border border-purple-200 hover:bg-purple-100 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-purple-900">{t('quickActions.pendingOffers')}</h3>
                  <p className="text-sm text-purple-700 mt-1">
                    {pendingOffers} {t('quickActions.offersNeedResponse')}
                  </p>
                </div>
                <ArrowRight className="text-purple-600" size={20} />
              </div>
            </Link>
          )}
        </div>
      )}

      <div className="card p-4 md:p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('quickActions.title')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Link
            to="/products/add"
            className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <Package className="text-custom-primary" size={24} />
            <span className="font-medium text-gray-900">{t('quickActions.addProduct')}</span>
          </Link>
          <Link
            to="/conversations"
            className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <MessageSquare className="text-custom-primary" size={24} />
            <span className="font-medium text-gray-900">{t('quickActions.viewMessages')}</span>
          </Link>
          <Link
            to="/orders"
            className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ShoppingCart className="text-custom-primary" size={24} />
            <span className="font-medium text-gray-900">{t('quickActions.viewOrders')}</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
